"use client";

import useCheckout from "@/app/hooks/useCheckout";
import Checkmark from "@/components/design-system/checkmark";
import Link from "next/link";
import { Fragment, MouseEvent, useMemo } from "react";
import style from "./checkoutNav.module.scss";

type StepKey = "license" | "shipping" | "billing" | "review";
type StepState = "completed" | "current" | "upcoming";

export default function CheckoutNav({ currentPage }: CheckoutNavProps) {
  const {
    readyForReview,
    checkoutIsLoading,
    licenseRequired,
    licenseStepCompleted,
    shippingStepCompleted,
    billingStepCompleted,
  } = useCheckout();

  const stepNumbers = useMemo(() => {
    if (checkoutIsLoading === true) {
      return { license: 0, shipping: 0, billing: 0, review: 0 };
    }
    if (licenseRequired) {
      return { license: 1, shipping: 2, billing: 3, review: 4 };
    }
    return { license: 0, shipping: 1, billing: 2, review: 3 };
  }, [checkoutIsLoading, licenseRequired]);

  const canGoToShipping = !licenseRequired || licenseStepCompleted;
  const canGoToBilling = shippingStepCompleted;
  const canGoToReview = readyForReview;

  const steps = useMemo<Step[]>(() => {
    const list: Step[] = [];
    if (licenseRequired) {
      list.push({ key: "license", label: "License", completed: licenseStepCompleted, enabled: true });
    }
    list.push({ key: "shipping", label: "Shipping", completed: shippingStepCompleted, enabled: canGoToShipping });
    list.push({ key: "billing", label: "Billing", completed: billingStepCompleted, enabled: canGoToBilling });
    list.push({ key: "review", label: "Review and Order", completed: canGoToReview, enabled: canGoToReview });
    return list;
  }, [
    licenseRequired,
    licenseStepCompleted,
    shippingStepCompleted,
    billingStepCompleted,
    canGoToShipping,
    canGoToBilling,
    canGoToReview,
  ]);

  // Index of the step the user is currently on; a segment (the line between two
  // steps) is blue only when the step to its LEFT sits strictly before this
  // index — i.e. it has been completed AND passed.
  const currentIndex = steps.findIndex((s) => s.key === currentPage);

  const linkHandler = (isEnabled: boolean) => (e: MouseEvent) => {
    if (!isEnabled) e.preventDefault();
  };

  return (
    <nav className={style.CheckoutNav} aria-label="Checkout progress">
      <ol className={style.track}>
        {steps.map((step, i) => {
          const state: StepState = step.key === currentPage ? "current" : step.completed ? "completed" : "upcoming";
          const hasLineBefore = i > 0;
          const hasLineAfter = i < steps.length - 1;
          // Segment to the left is blue when the previous step is passed; segment
          // to the right is blue when this step is passed.
          const lineBeforeBlue = hasLineBefore && i - 1 < currentIndex;
          const lineAfterBlue = hasLineAfter && i < currentIndex;
          return (
            <Fragment key={step.key}>
              {hasLineBefore && (
                <li aria-hidden className={`${style.connector} ${lineBeforeBlue ? style.connectorCompleted : ""}`} />
              )}
              <li
                className={[
                  style.stepItem,
                  hasLineBefore ? style.hasLineBefore : "",
                  lineBeforeBlue ? style.lineBeforeCompleted : "",
                  hasLineAfter ? style.hasLineAfter : "",
                  lineAfterBlue ? style.lineAfterCompleted : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Link
                  href={step.enabled ? `/checkout/${step.key}` : {}}
                  onClick={linkHandler(step.enabled)}
                  className={`${style.step} ${style[state]}`}
                  aria-current={step.key === currentPage ? "step" : undefined}
                >
                  {state === "completed" ? (
                    <Checkmark size="medium" className={style.checkBadge} />
                  ) : (
                    <span className={style.badge}>{stepNumbers[step.key] || ""}</span>
                  )}
                  <span className={style.label}>{step.label}</span>
                </Link>
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

type Step = {
  key: StepKey;
  label: string;
  completed: boolean;
  enabled: boolean;
};

export type CheckoutNavProps = {
  currentPage: "license" | "shipping" | "billing" | "review";
};
